import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAutocomplete,
  MiBox,
  MiButton,
  MiIcon,
  MiIconButton,
} from "@miplatform/components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";
import {
  mdiCheck,
  mdiPencilOutline,
  mdiPlus,
  mdiTrashCanOutline,
  mdiWindowClose,
} from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import {
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import { useHoldReasonsViewModel } from "./useHoldReasonsViewModel";

export const HoldReasons = ({ isLoading, orderTemplate, onUpdate }) => {
  const {
    methods,
    holdReasonOptions,
    holdReasonsForm,
    getHoldReasons,
    handleReset,
  } = useHoldReasonsViewModel({ orderTemplate });
  const { trigger } = methods;
  const { errors } = methods.formState;
  const { fields, append, remove, update } = holdReasonsForm;
  const { classes, theme } = useStyles();
  const [editingIndex, setEditingIndex] = useState(-1);
  const [autocompleteValue, setAutocompleteValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [editValue, setEditValue] = useState("");

  const handleAddItem = () => {
    if (inputValue.trim()) {
      append({ value: inputValue.trim() });
      trigger();
      setInputValue("");
      setAutocompleteValue(null);
    }
  };

  const handleDeleteItem = (index) => {
    remove(index);
    trigger();
  };

  const startEditing = (index, field) => {
    setEditingIndex(index);
    setEditValue(field.value);
  };

  const saveEdit = (index) => {
    if (editValue.trim()) {
      update(index, { value: editValue.trim() });
      trigger();
      setEditingIndex(-1);
      setEditValue("");
    }
  };
  useEffect(() => {
    getHoldReasons();
  }, []);

  useEffect(() => {
    handleReset();
  }, [orderTemplate]);

  return (
    <>
      <MiBox>
        <Box className={classes.reasonInputContainer}>
          <MiAutocomplete
            fullWidth
            size="small"
            options={holdReasonOptions}
            value={autocompleteValue}
            onChange={(_, value) => {
              if (value) {
                setAutocompleteValue(value);
                setInputValue(value);
              }
            }}
            renderInput={(params) => (
              <TextField
                fullWidth
                value={inputValue}
                placeholder="Add new hold reason"
                variant="outlined"
                {...params}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddItem();
                  }
                }}
              />
            )}
          />
          <MiButton
            variant={BUTTON_VARIANTS.OUTLINED}
            onClick={handleAddItem}
            title="ADD"
            startIcon={{
              path: mdiPlus,
              iconSize: 0.8,
            }}
          />
        </Box>
        <List className={classes.listItemContainer}>
          {fields.map((field, index) => (
            <ListItem
              key={field.id}
              className={`${classes.listItem} ${
                errors?.holdReasons?.[index]?.value ? classes.listItemError : ""
              }`}
            >
              {editingIndex === index ? (
                <TextField
                  fullWidth
                  size="small"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  variant="outlined"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      saveEdit(index);
                    }
                  }}
                />
              ) : (
                <ListItemText
                  primary={field.value}
                  secondary={errors?.holdReasons?.[index]?.value?.message}
                  secondaryTypographyProps={{
                    color: errors?.holdReasons?.[index]?.value
                      ? "error.main"
                      : "text.secondary",
                  }}
                  className={classes.listItemText}
                />
              )}
              <ListItemSecondaryAction>
                {editingIndex === index ? (
                  <>
                    <MiIconButton
                      data-testid="save-edit-button"
                      onClick={() => saveEdit(index)}
                      className={classes.iconHover}
                    >
                      <MiIcon
                        color={theme.palette.primary.main}
                        path={mdiCheck}
                      />
                    </MiIconButton>
                    <MiIconButton
                      data-testid="close-edit-button"
                      onClick={() => setEditingIndex(-1)}
                      className={classes.iconHover}
                    >
                      <MiIcon
                        color={theme.palette.red.main}
                        path={mdiWindowClose}
                      />
                    </MiIconButton>
                  </>
                ) : (
                  <>
                    <MiIconButton
                      data-testid="edit-button"
                      onClick={() => startEditing(index, field)}
                      className={classes.iconHover}
                    >
                      <MiIcon
                        color={theme.palette.black.main}
                        path={mdiPencilOutline}
                      />
                    </MiIconButton>
                    <MiIconButton
                      data-testid="delete-button"
                      onClick={() => handleDeleteItem(index)}
                      className={classes.iconHover}
                    >
                      <MiIcon
                        path={mdiTrashCanOutline}
                        color={BUTTON_COLORS.RED}
                      />
                    </MiIconButton>
                  </>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </MiBox>
      <Footer
        show={methods.formState.isDirty}
        handleCancelClick={handleReset}
        handleSaveClick={methods.handleSubmit((data) =>
          onUpdate({
            ...orderTemplate,
            holdReasons: data.holdReasons.map(({ value }) => value),
          })
        )}
        disableSave={!methods.formState.isValid || isLoading}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  inputContainer: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  reasonInputContainer: {
    display: "flex",
    gap: theme.spacing(4),
    marginBottom: theme.spacing(4),
    flex: "0 0 auto",
  },
  iconHover: {
    transition: "transform 0.2s",
    "&:hover": { transform: "scale(1.2)" },
  },
  listItem: {
    backgroundColor: theme.palette.primary.light,
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    height: 60,
  },
  listItemError: {
    border: `1px solid ${theme.palette.error.main}`,
  },
  listItemText: {
    wordBreak: "break-word",
    "& .MuiListItemText-primary": {
      wordBreak: "break-word",
    },
  },
  listItemContainer: {
    flex: "1 1 auto",
    overflowY: "auto",
    paddingRight: theme.spacing(2),
  },
}));
